import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import { useLiveScreenSubscription } from '../../hooks/useLiveScreenSubscription';

const LIVE_WALL_PERMISSION = 'monitoring.live_wall.read';
const LIVE_SCREEN_CONFIGURE = 'monitoring.live_screen.configure';
const POLL_MS = 18_000;
/** Target tile width — auto-fill packs as many columns as the viewport allows. */
const TILE_MIN_WIDTH_PX = 220;

type LiveWallRow = {
  id: string;
  device_id: string;
  device_name: string;
  online: boolean;
  employee?: { id?: string; name?: string; employee_id?: string } | null;
  campaign?: { id?: string; name?: string } | string | null;
  clock_status?: string | null;
  work_state?: string | null;
  windows_locked?: boolean;
  live_screen_allowed?: boolean;
  live_screen_device_enabled?: boolean;
  live_screen_global_enabled?: boolean;
  live_screen_unavailable_reason?: string | null;
  live_screen_unavailable_text?: string | null;
  live_screen_last_frame_at?: string | null;
};

function campaignLabel(campaign: LiveWallRow['campaign']): string {
  if (!campaign) return '—';
  if (typeof campaign === 'string') return campaign;
  return campaign.name || '—';
}

function overlayLabel(row: LiveWallRow, connecting: boolean, stale: boolean, hasFrame: boolean): string | null {
  if (row.live_screen_device_enabled === false) return 'VIEW OFF';
  if (row.live_screen_unavailable_reason === 'GLOBAL_DISABLED' || row.live_screen_unavailable_reason === 'DISABLED') {
    return 'LIVE SCREEN DISABLED';
  }
  if (row.live_screen_unavailable_reason === 'DEVICE_DISABLED') return 'LIVE SCREEN DISABLED';
  if (row.windows_locked || row.live_screen_unavailable_reason === 'WINDOWS_LOCKED') return 'WINDOWS LOCKED';
  if (row.live_screen_unavailable_reason === 'AGENT_UNSUPPORTED') return 'AGENT UPDATE REQUIRED';
  if (row.live_screen_unavailable_reason === 'AGENT_OFFLINE') return 'AGENT OFFLINE';
  // Attendance / work-state overlays must NOT block Live Screen tiles.
  if (row.live_screen_unavailable_reason && !row.live_screen_allowed) {
    const attendanceReasons = new Set([
      'LUNCH', 'BREAK', 'BATHROOM', 'IDLE', 'CLOCKED_OUT', 'PRE_CLOCK_IN', 'EMERGENCY_BYPASS', 'NO_SCHEDULE',
    ]);
    if (attendanceReasons.has(row.live_screen_unavailable_reason)) {
      // Ignore stale attendance deny reasons — stream if otherwise allowed.
    } else {
      return (row.live_screen_unavailable_text || row.live_screen_unavailable_reason).toUpperCase();
    }
  }
  if (connecting && !hasFrame) return 'CONNECTING';
  if (stale) return 'STREAM STALE';
  return null;
}

const LiveWallTile: React.FC<{
  row: LiveWallRow;
  subscribeEnabled: boolean;
  staleThresholdSeconds: number;
  canConfigureLiveScreen: boolean;
  onOpen: () => void;
  onToggleView: (deviceId: string, enabled: boolean) => void;
  togglePending: boolean;
}> = ({
  row,
  subscribeEnabled,
  staleThresholdSeconds,
  canConfigureLiveScreen,
  onOpen,
  onToggleView,
  togglePending,
}) => {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const setRef = useCallback((node: HTMLDivElement | null) => {
    setEl(node);
  }, []);

  const viewEnabled = row.live_screen_device_enabled !== false;
  const globalEnabled = row.live_screen_global_enabled !== false;

  const { frameUrl, lastFrameAt, connecting } = useLiveScreenSubscription({
    deviceId: row.device_id,
    mode: 'THUMBNAIL',
    enabled: subscribeEnabled && row.online && viewEnabled && Boolean(row.live_screen_allowed),
    pauseWhenNotVisible: true,
    observeTarget: el,
  });

  const stale = Boolean(
    lastFrameAt
    && Date.now() - lastFrameAt.getTime() > staleThresholdSeconds * 1000,
  );
  const overlay = overlayLabel(row, connecting, stale, Boolean(frameUrl));
  const subtitle = [
    row.employee?.name || 'Not currently identified',
    campaignLabel(row.campaign),
  ].join(' · ');
  const teksStatus = [row.clock_status, row.work_state].filter(Boolean).join(' · ') || '—';
  const tooltipParts = [
    `Computer: ${row.device_name}`,
    `Employee: ${row.employee?.name || 'Not currently identified'}`,
    `Campaign: ${campaignLabel(row.campaign)}`,
    `TEKS Time status: ${teksStatus}`,
    viewEnabled ? 'Live view: On' : 'Live view: Off',
  ];
  if (overlay) tooltipParts.push(`Stream: ${overlay}`);
  const tileTooltip = tooltipParts.join('\n');

  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{tileTooltip}</Box>} arrow placement="top" enterDelay={400}>
      <Box
        data-testid={`live-wall-tile-${row.device_id}`}
        onClick={onOpen}
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: 1.5,
          overflow: 'hidden',
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <Box
          ref={setRef}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 10',
            bgcolor: '#1a1a1a',
            overflow: 'hidden',
          }}
        >
          {frameUrl ? (
            <Box
              component="img"
              src={frameUrl}
              alt={`${row.device_name} screen`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                opacity: overlay ? 0.4 : 1,
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                bgcolor: '#111',
              }}
            >
              <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center', fontWeight: 600 }}>
                {overlay || (subscribeEnabled ? 'CONNECTING' : 'PAUSED')}
              </Typography>
            </Box>
          )}

          {overlay && frameUrl && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.35)',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.4 }}>
                {overlay}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            px: 1,
            py: 0.75,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            minHeight: 52,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'success.main',
              flexShrink: 0,
              mt: 0.55,
            }}
          />
          <Box minWidth={0} flex={1}>
            <Typography
              variant="caption"
              noWrap
              sx={{ fontWeight: 700, lineHeight: 1.2, display: 'block' }}
            >
              {row.device_name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ fontSize: '0.65rem', lineHeight: 1.15, display: 'block' }}
            >
              {subtitle}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ fontSize: '0.65rem', lineHeight: 1.15, display: 'block' }}
            >
              {teksStatus}
            </Typography>
          </Box>
          {canConfigureLiveScreen && globalEnabled && (
            <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} flexShrink={0} mt={0.25}>
              <FormControlLabel
                sx={{ m: 0, mr: 0, gap: 0.25 }}
                control={(
                  <Switch
                    size="small"
                    checked={viewEnabled}
                    disabled={togglePending}
                    inputProps={{ 'aria-label': `Live view for ${row.device_name}` }}
                    data-testid={`live-wall-view-toggle-${row.device_id}`}
                    onChange={(e) => onToggleView(row.device_id, e.target.checked)}
                    sx={{ my: -0.75 }}
                  />
                )}
                label={(
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {viewEnabled ? 'On' : 'Off'}
                  </Typography>
                )}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};


const LiveWallPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes(LIVE_WALL_PERMISSION);
  const canConfigureLiveScreen = permissions.includes(LIVE_SCREEN_CONFIGURE);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [workState, setWorkState] = useState('');

  const queryParams = useMemo(() => ({
    search: search || undefined,
    status: status || undefined,
    workState: workState || undefined,
  }), [search, status, workState]);

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['monitoringLiveWall', queryParams],
    queryFn: () => apiClient.monitoring.getLiveWall(queryParams),
    enabled: canRead,
    refetchInterval: POLL_MS,
  });

  const toggleViewMutation = useMutation({
    mutationFn: ({ deviceId, enabled }: { deviceId: string; enabled: boolean }) =>
      apiClient.monitoring.updateDeviceLiveScreen(deviceId, { enabled }),
    onMutate: async ({ deviceId, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ['monitoringLiveWall'] });
      const previous = queryClient.getQueriesData({ queryKey: ['monitoringLiveWall'] });
      queryClient.setQueriesData({ queryKey: ['monitoringLiveWall'] }, (current: any) => {
        if (!current?.data?.data) return current;
        const patchRow = (row: LiveWallRow): LiveWallRow => {
          if (row.device_id !== deviceId) return row;
          if (!enabled) {
            return {
              ...row,
              live_screen_device_enabled: false,
              live_screen_allowed: false,
              live_screen_unavailable_reason: 'DEVICE_DISABLED',
            };
          }
          return {
            ...row,
            live_screen_device_enabled: true,
            live_screen_allowed: row.online ? true : row.live_screen_allowed,
            live_screen_unavailable_reason:
              row.live_screen_unavailable_reason === 'DEVICE_DISABLED'
                ? null
                : row.live_screen_unavailable_reason,
          };
        };
        return {
          ...current,
          data: {
            ...current.data,
            data: {
              ...current.data.data,
              online: (current.data.data.online || []).map(patchRow),
              offline: (current.data.data.offline || []).map(patchRow),
            },
          },
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, value]: [unknown, unknown]) => {
        queryClient.setQueryData(key as any, value);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoringLiveWall'] });
      queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
    },
  });

  const payload = data?.data?.data;
  const online: LiveWallRow[] = payload?.online || [];
  const offline: LiveWallRow[] = payload?.offline || [];
  const summary = payload?.summary || { online: 0, offline: 0, working: 0, idle: 0 };
  const maxActiveThumbnails = Number(payload?.config?.maxActiveThumbnails || 24);
  const staleThresholdSeconds = Number(payload?.config?.staleFrameThresholdSeconds || 8);
  const featureEnabled = payload?.config?.enabled !== false;

  const activeSubscribeIds = useMemo(
    () => online
      .filter((r) => r.live_screen_allowed && r.live_screen_device_enabled !== false)
      .map((r) => r.device_id)
      .slice(0, maxActiveThumbnails),
    [online, maxActiveThumbnails],
  );

  if (!canRead) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography color="error">
          You do not have permission to view Workforce View (`monitoring.live_wall.read`).
        </Typography>
      </Box>
    );
  }

  const openDevice = (row: LiveWallRow) => {
    navigate(`/workforce-monitoring/computers/${row.id}?tab=live-screen`);
  };

  const emptyAll = !isLoading && online.length === 0 && offline.length === 0;
  const filtersActive = Boolean(search || status || workState);
  const showOffline = status !== 'online' && offline.length > 0;
  const showOnline = status !== 'offline';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" mb={2}>
        <Box>
          <Typography variant="h6" data-testid="workforce-view-heading">Workforce View</Typography>
          <Typography variant="body2" color="text.secondary">
            Workforce View shows live previews of available employee computers.
            Open a computer to view its Live Screen.
            {' · '}{summary.online ?? 0} online · {summary.offline ?? 0} offline
            {summary.live_available != null ? ` · ${summary.live_available} live available` : ''}
          </Typography>
          {!featureEnabled && (
            <Typography variant="caption" color="warning.main" display="block" mt={0.5}>
              Live Screen is disabled in configuration.
            </Typography>
          )}
        </Box>
        <Button size="small" startIcon={<RefreshIcon />} variant="outlined" onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </Button>
      </Box>

      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
          placeholder="Computer, employee…"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(String(e.target.value))}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Work State</InputLabel>
          <Select label="Work State" value={workState} onChange={(e) => setWorkState(String(e.target.value))}>
            <MenuItem value="">All</MenuItem>
            {['WORKING', 'WORKING_COVERAGE', 'LUNCH', 'BREAK', 'BATHROOM', 'IDLE_GRACE', 'LOCKED_IDLE', 'CLOCKED_OUT'].map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography color="error">Failed to load Workforce View.</Typography>
        </Paper>
      )}

      {isLoading ? (
        <CircularProgress size={24} />
      ) : emptyAll ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            {filtersActive
              ? 'No computers match the selected filters.'
              : 'No TEKS TIME computers are available for Workforce View yet.'}
          </Typography>
        </Paper>
      ) : (
        <>
          {showOnline && (
            <Box mb={3}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, letterSpacing: 1.2, fontWeight: 700 }}
              >
                Online ({online.length})
                {activeSubscribeIds.length < online.filter((r) => r.live_screen_allowed && r.live_screen_device_enabled !== false).length
                  ? ` · streaming up to ${maxActiveThumbnails}`
                  : ''}
              </Typography>
              {online.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {filtersActive ? 'No online computers match filters.' : 'No computers are online right now.'}
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.25,
                    gridTemplateColumns: {
                      xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                      sm: `repeat(auto-fill, minmax(${TILE_MIN_WIDTH_PX}px, 1fr))`,
                    },
                  }}
                >
                  {online.map((row) => (
                    <LiveWallTile
                      key={row.id}
                      row={row}
                      subscribeEnabled={activeSubscribeIds.includes(row.device_id)}
                      staleThresholdSeconds={staleThresholdSeconds}
                      canConfigureLiveScreen={canConfigureLiveScreen}
                      onOpen={() => openDevice(row)}
                      onToggleView={(deviceId, enabled) => toggleViewMutation.mutate({ deviceId, enabled })}
                      togglePending={toggleViewMutation.isPending}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" display="block" mt={1.25}>
                Each live screen is view-only at reduced quality. Offline computers appear as labels below until they come back online.
              </Typography>
            </Box>
          )}

          {showOffline && (
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, letterSpacing: 1.2, fontWeight: 700 }}
              >
                Offline ({offline.length})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {offline.map((row) => (
                  <Tooltip
                    key={row.id}
                    arrow
                    placement="top"
                    enterDelay={400}
                    title={(
                      <Box sx={{ whiteSpace: 'pre-line' }}>
                        {[
                          `Computer: ${row.device_name}`,
                          `Employee: ${row.employee?.name || 'Unassigned'}`,
                          `Campaign: ${campaignLabel(row.campaign)}`,
                          `TEKS Time status: ${[row.clock_status, row.work_state].filter(Boolean).join(' · ') || '—'}`,
                          'Status: Offline — full live tile appears when online',
                        ].join('\n')}
                      </Box>
                    )}
                  >
                    <Box
                      onClick={() => openDevice(row)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.6,
                        borderRadius: 2,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        maxWidth: '100%',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500, maxWidth: 280 }}>
                        {row.device_name}
                      </Typography>
                      {canConfigureLiveScreen && row.live_screen_global_enabled !== false && (
                        <Box onClick={(e) => e.stopPropagation()} display="flex" alignItems="center">
                          <Switch
                            size="small"
                            checked={row.live_screen_device_enabled !== false}
                            disabled={toggleViewMutation.isPending}
                            inputProps={{ 'aria-label': `Live view for ${row.device_name}` }}
                            onChange={(e) => toggleViewMutation.mutate({
                              deviceId: row.device_id,
                              enabled: e.target.checked,
                            })}
                            sx={{ my: -0.5 }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default LiveWallPage;
